import axios from "axios";
import _ from "lodash";
import moment from "moment";
import "moment/locale/pt-br";

// Services
import getApiUrl from "services/getApiUrl";
import { exportApi } from "services/actions/export";

// Actions
import { showNotification } from "./notification";

// Utils
import buildFilterEntryKeepField from "utils/normalize-field-to-request-filter";
import getAllPersistentFilterByReq from "utils/get-field-by-request";
import { normalizeTimeFilterValue } from "utils/use-persistents-filters";

// Types
const FETCH_FORM_EXPORT_DATA_REQUEST = "core/form/FETCH_FORM_EXPORT_DATA_REQUEST";
const FETCH_FORM_DATA_EXPORT_SUCCESS = "core/form/FETCH_FORM_DATA_EXPORT_SUCCESS";
const FETCH_FORM_DATA_EXPORT_FAILURE = "core/form/FETCH_FORM_DATA_EXPORT_FAILURE";

const getFieldsColumns = (columns, formName) => {
  if (!formName || !columns || columns.length === 0) {
    return [];
  }

  const config = columns
    .filter(c => !c.hideInSearch)
    .map(c => buildFilterEntryKeepField(c.field ?? c.title, c, formName));
  return config;
};

// Reducer
const initialState = {
  loading: false,
  dialogText: "",
};

export default function exportFormReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_FORM_EXPORT_DATA_REQUEST:
      return { ...state, loading: true };
    case FETCH_FORM_DATA_EXPORT_SUCCESS:
      return {
        ...state,
        loading: false,
        dialogText: action.payload.dialogText,
      };
    case FETCH_FORM_DATA_EXPORT_FAILURE:
      return {
        ...state,
        loading: false,
        dialogText: action.payload,
      };
    default:
      return state;
  }
}

// Actions
export const exportFormData = (
  form,
  page,
  pageSize,
  createdAt,
  queryParams = {},
  createdBy,
  sorter = null,
  location = null,
  navigate = null,
  formRef = null,
  isRegistryForm = false,
  queueIdOverride = null
) => {
  return async (dispatch) => {
    // dispatch({ type: FETCH_FORM_EXPORT_DATA_REQUEST }); // Disable table loading, use Modal instead
    try {
      const fields = await getApiUrl.get(`/objetos/${form}/campos/todos?withRef=true`);
      const { message: fieldsMessage } = fields.data;
      const sortBy = [];
      if (sorter && Object.entries(sorter).length > 0) {
        for (const key in sorter) {
          if (!sorter[key]) continue;
          let index = fieldsMessage.findIndex(item => item.field === key);
          if (index !== -1) {
            sortBy.push({
              field: fieldsMessage[index]['field'],
              sort: sorter[key],
              fieldType: fieldsMessage[index]['fieldtype'],
              returnType: fieldsMessage[index]['returntype'] || null
            });
          } else {
            sortBy.push({
              field: key,
              sort: sorter[key]
            });
          }
        }
      }
      const query = [];

      // Verifica se existe campos
      if (fieldsMessage) {
        if (isRegistryForm && location && navigate && formRef) {
          // Inclui o filtro por ID do registro (_id), que não é um campo dinâmico do formulário
          const columnsFilter = [
            buildFilterEntryKeepField("_id", {}, form),
            ...getFieldsColumns(fieldsMessage, form),
          ];
          const persistentFilters = getAllPersistentFilterByReq(columnsFilter, location, navigate, formRef);
          queryParams = {
            ...persistentFilters,
            ...queryParams
          };
        }
        // Array com nome de todos os campos
        const fieldNames = fieldsMessage.map((y) => y.field);

        // Verifica se está filtrando a tabela
        if (Object.keys(queryParams).length > 0) {
          fieldsMessage.map(async (data) => {
            const { field: fieldName, fieldtype: fieldType } = data;
            if (queryParams[fieldName]) {
              let fieldMapping;
              switch (fieldType) {
                case "Text":
                  fieldMapping = {
                    nested: {
                      path: fieldName,
                      query: {
                        match: {
                          [`${fieldName}.value.keyword`]: queryParams[fieldName] ?? "",
                        },
                      },
                    },
                  }
                  break;
                case "Time":
                  // O registro armazena o tempo como "HH:mm"; normaliza o valor
                  // do filtro (moment/timestamp em ms/string) para o mesmo formato
                  fieldMapping = {
                    nested: {
                      path: fieldName,
                      query: {
                        match: {
                          [`${fieldName}.value.keyword`]: normalizeTimeFilterValue(queryParams[fieldName]) ?? "",
                        },
                      },
                    },
                  }
                  break;
                case "Numeric":
                  fieldMapping = {
                    nested: {
                      path: fieldName,
                      query: {
                        match: {
                          [`${fieldName}.value`]: queryParams[fieldName] ?? "",
                        },
                      },
                    },
                  }
                  break
                case "Date":
                  if (_.isArray(queryParams[fieldName])) {
                    const [start, end] = queryParams[fieldName];
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          script: {
                            script: {
                              source: `
                  if (!doc.containsKey(params.field) || doc[params.field].empty) return false;
                  String dateString = doc[params.field].value;
                  if (dateString == '') return false;
                  ZonedDateTime fieldDate = ZonedDateTime.parse(dateString);
                  ZonedDateTime startDate = ZonedDateTime.parse(params.startDate);
                  ZonedDateTime endDate = ZonedDateTime.parse(params.endDate);
                  return !fieldDate.isBefore(startDate) && !fieldDate.isAfter(endDate);
                `,
                              params: {
                                field: `${fieldName}.value.keyword`,
                                startDate: moment.utc(start).startOf('day').toISOString(),
                                endDate: moment.utc(end).startOf('day').toISOString()
                              },
                              lang: "painless"
                            }
                          }
                        }
                      }
                    };
                  } else {
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          match: {
                            [`${fieldName}.value.keyword`]: moment(queryParams[fieldName], 'DD/MM/YYYY').startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]') ?? "",
                          },
                        },
                      },
                    }
                  }
                  break;
                case "refLookup":
                  if (_.isString(queryParams[fieldName]) || _.isNumber(queryParams[fieldName])) {
                    const value = moment(queryParams[fieldName], 'DD/MM/YYYY', true).isValid() ? moment(queryParams[fieldName], 'DD/MM/YYYY').format('YYYY-MM-DDT00:00:00[Z]') : queryParams[fieldName] ?? "";
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          match: {
                            [`${fieldName}.value.keyword`]: value,
                          },
                        },
                      },
                    }
                  } else if (_.isArray(queryParams[fieldName])) {
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          terms: {
                            [`${fieldName}.value`]: queryParams[fieldName],
                          },
                        },
                      },
                    }
                  }

                  break;
                case "uniqueSelection":
                case "multipleSelection":
                  let terms = (_.isArray(queryParams[fieldName]) ? queryParams[fieldName] : [queryParams[fieldName]])
                    .filter((item) => item !== undefined && item !== null && item !== "");
                  if (terms.length > 0) {
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          terms: {
                            [`${fieldName}.value.keyword`]: terms,
                          },
                        },
                      },
                    };
                  } else {
                    return;
                  }

                  break;
                case "refUniqueSelection":
                case "refMultipleSelection":
                  if (_.isArray(queryParams[fieldName])) {
                    const refTerms = queryParams[fieldName].map((item) => {
                      if (moment(item, 'DD/MM/YYYY', true).isValid()) return moment(item, 'DD/MM/YYYY').format('YYYY-MM-DDT00:00:00[Z]');
                      return item;
                    }).filter((item) => item !== undefined && item !== null && item !== "");
                    
                    if (refTerms.length > 0) {
                      fieldMapping = {
                        nested: {
                          path: fieldName,
                          query: {
                            terms: {
                              [`${fieldName}.value.keyword`]: refTerms,
                            },
                          },
                        },
                      };
                    } else {
                      return;
                    }
                  } else {
                    const value = moment(queryParams[fieldName], 'DD/MM/YYYY', true).isValid() ? moment(queryParams[fieldName], 'DD/MM/YYYY').format('YYYY-MM-DDT00:00:00[Z]') : queryParams[fieldName] ?? "";
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          match: {
                            [`${fieldName}.value.keyword`]: value,
                          },
                        },
                      },
                    };
                  }
                  break;
                case "formula":
                  if (data.returntype === "Numeric") {
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          match: {
                            [`${fieldName}.value`]: queryParams[fieldName] ?? "",
                          },
                        },
                      },
                    };
                  } else {
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          match: {
                            [`${fieldName}.value.keyword`]: queryParams[fieldName],
                          },
                        },
                      },
                    };
                  }
                  break;
                default:
                  fieldMapping = {}
              }
              query.push(fieldMapping);
            }
          });
          const recordIdFilter = _.trim(queryParams._id || queryParams.id || "");
          if (recordIdFilter) {
            query.push({
              term: { _id: recordIdFilter }
            });
          }
        }

        let exportCreatedAt = createdAt;
        if (!exportCreatedAt && queryParams?.createdAt) {
          exportCreatedAt = queryParams.createdAt;
        }
        if (!exportCreatedAt && formRef?.current) {
          if (typeof formRef.current.getFieldValue === 'function') {
            exportCreatedAt = formRef.current.getFieldValue("createdAt");
          } else if (typeof formRef.current.getFieldsValue === 'function') {
            const values = formRef.current.getFieldsValue();
            if (values) exportCreatedAt = values.createdAt;
          }
        }

        const queueId = queueIdOverride || `export_${Date.now()}`;

        const startExport = await exportApi.exportCsv(
          {
            formName: form,
            query,
            createdAt: exportCreatedAt,
            showOnly: [...fieldNames, "createdAt", 'updatedAt'],
            createdBy,
            sortBy: sortBy,
            isFilter: (Object.keys(queryParams).length > 0 || exportCreatedAt),
            queueId: queueId
          }
        );

        if (startExport && startExport.data.success) {
          const { message: dialogMessage, downloadUrl } = startExport.data;

          dispatch({
            type: FETCH_FORM_DATA_EXPORT_SUCCESS,
            payload: {
              dialogText: dialogMessage,
            },
          });

          dispatch(showNotification(dialogMessage, "success"));

          return startExport.data;
        } else {
          const errorMsg = startExport?.data?.message || "Nada encontrado, tente novamente!";
          dispatch({
            type: FETCH_FORM_DATA_EXPORT_FAILURE,
            payload: errorMsg,
          });
          return { success: false, error: errorMsg };
        }
      } else {
        dispatch({
          type: FETCH_FORM_DATA_EXPORT_FAILURE,
          payload: "Nada encontrado, tente novamente!",
        });
        return { success: false, error: "Nada encontrado, tente novamente!" };
      }
    } catch (error) {
      console.error("Export Error:", error);
      const errorMsg = error?.response?.data?.message || error.message;
      dispatch({ type: FETCH_FORM_DATA_EXPORT_FAILURE, payload: errorMsg });
      return { success: false, error: errorMsg };
    }
  };
};
